import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { IFilter, IPopulate, IQueryParam, IRol } from 'modelos/src';
import { Subscription } from 'rxjs';
import {
  IRegExpSearch,
  IFiltroTabla,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { RolsService } from '../roles.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { TOTAL_ACCIONES } from '../roles.constants';

import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';
import { barraHtml } from '../../../../../auxiliares/tabla/celdas';
import { TonoEstado } from '../../../../../auxiliares/tabla/tabla.interfaces';

@Component({
  selector: 'app-listado-roles',
  templateUrl: './listado-roles.component.html',
  styleUrl: './listado-roles.component.scss',
  standalone: false,
})
export class ListadoRolesComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private navigator = inject(Router);
  private route = inject(ActivatedRoute);
  private dialogService = inject(DialogService);
  private service = inject(RolsService);

  //// Tabla nueva
  public name = 'RolsComponent';
  public datos?: IRol[];
  public rols$?: Subscription;
  public totalCount = 0;
  public columnas?: IColumnas<IRol>[];
  public botonCrear: ICrearBoton = {
    mostrar: true, // Agregar logica nueva cuando esté disponible
    tooltip: 'Crear rol',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };
  public search: IRegExpSearch = {
    fields: ['nombre'],
  };
  public filtros: IFiltroTabla[] = [];

  public filter: IFilter<IRol> = {};
  public populate: IPopulate = {};
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
  };

  public async listar(): Promise<void> {
    this.rols$?.unsubscribe();
    this.rols$ = this.lista2.roles(this.query, 60).subscribe((data) => {
      this.totalCount = data.totalCount;
      this.datos = data.datos;
    });
  }

  // Acciones
  public async crear() {
    this.navigator.navigate(['crear'], { relativeTo: this.route });
  }

  public async editar(dato: IRol) {
    this.navigator.navigate(['editar', dato._id], {
      relativeTo: this.route,
    });
  }

  public async eliminar(dato: IRol) {
    const res = await this.dialogService.confirm(
      'Eliminar Rol',
      `¿Querés eliminar el Rol ${dato.nombre}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(dato._id!);
      this.helper.notifSuccess('Rol eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas() {
    const columnas: IColumnas<IRol>[] = [
      // Identificacion
      {
        header: {
          label: 'Nombre',
          sortable: true,
          sort: 'nombre',
        },
        row: {
          // Rol global: con su ícono y el texto (antes sólo el ícono)
          html: (dato) => {
            if (!dato.nombre && !dato.global) return '';
            let html = `<div class="tabla-celda-col">`;
            html += dato.nombre
              ? `<span class="tabla-principal">${escaparHtml(dato.nombre)}</span>`
              : `<span class="no-info">Sin nombre</span>`;
            if (dato.global) {
              html += `<div class="tabla-linea"><span class="material-symbols-outlined">public</span><span>Global</span></div>`;
            }
            return html + `</div>`;
          },
          noData: 'Sin nombre',
        },
      },
      // Permisos seleccionados
      {
        header: {
          label: 'Permisos',
          sortable: false,
        },
        row: {
          // Cantidad de permisos sobre el total, con la escala de color de
          // siempre (cuánto acceso da el rol): gris ninguno, verde pocos,
          // naranja la mitad, rojo casi todos. La barra muestra la proporción.
          html: (dato) => {
            const cantidad = dato.acciones?.length ?? 0;
            const porcentaje = Math.round((cantidad / TOTAL_ACCIONES) * 100);
            const tono: TonoEstado =
              porcentaje === 0
                ? 'neutro'
                : porcentaje < 30
                  ? 'ok'
                  : porcentaje < 70
                    ? 'aviso'
                    : 'error';
            return barraHtml(porcentaje, {
              tono,
              texto: `${cantidad} / ${TOTAL_ACCIONES}`,
            });
          },
          tooltip: (dato) => {
            const cantidad = dato.acciones?.length ?? 0;
            const porcentaje = Math.round((cantidad / TOTAL_ACCIONES) * 100);
            return `${cantidad} de ${TOTAL_ACCIONES} permisos (${porcentaje} %). Verde: pocos; naranja: la mitad; rojo: casi todos.`;
          },
        },
      },
    ];

    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: (dato) => {
              if (
                !this.helper.tienePermiso('Administración - Editar roles') ||
                (dato.global && !this.helper.esNivel0())
              ) {
                return true;
              }
              return false;
            },
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: (dato) => {
              if (
                !this.helper.tienePermiso('Administración - Eliminar roles') ||
                (dato.global && !this.helper.esNivel0())
              ) {
                return true;
              }
              return false;
            },
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy() {
    this.rols$?.unsubscribe();
  }
}
